/**
 * Centralized enum labels for human-readable display across the application
 * 
 * This file provides human-friendly labels for all enum values,
 * ensuring consistent terminology throughout the UI.
 */

import { APP_ENUMS } from './enums';
import { getRoleDisplayName } from './roles';
import type { UserRole } from '../types/Auth';

export const ENUM_LABELS = {
  // User roles (using existing role display names)
  userRoles: Object.fromEntries(
    Object.entries(APP_ENUMS.userRoles).map(([, role]) => [
      role, 
      getRoleDisplayName(role as UserRole)
    ])
  ) as Record<string, string>,
  
  userStatus: {
    [APP_ENUMS.userStatus.ACTIVE]: 'Active',
    [APP_ENUMS.userStatus.INACTIVE]: 'Inactive',
    [APP_ENUMS.userStatus.PENDING]: 'Pending Activation',
    [APP_ENUMS.userStatus.SUSPENDED]: 'Suspended',
  },
  
  organizationTypes: {
    [APP_ENUMS.organizationTypes.INSURER]: 'Insurance Company',
    [APP_ENUMS.organizationTypes.REINSURER]: 'Reinsurance Company',
    [APP_ENUMS.organizationTypes.BROKER]: 'Insurance Broker',
    [APP_ENUMS.organizationTypes.AGENT]: 'Insurance Agent',
  },
  
  organizationStatus: {
    [APP_ENUMS.organizationStatus.ACTIVE]: 'Active',
    [APP_ENUMS.organizationStatus.INACTIVE]: 'Inactive',
    [APP_ENUMS.organizationStatus.PENDING_APPROVAL]: 'Pending Approval',
  },
  
  productTypes: {
    [APP_ENUMS.productTypes.CROP]: 'Crop Insurance',
    [APP_ENUMS.productTypes.WEATHER]: 'Weather Insurance',
    [APP_ENUMS.productTypes.PARAMETRIC]: 'Parametric Insurance',
    [APP_ENUMS.productTypes.LIVESTOCK]: 'Livestock Insurance',
  },
  
  productStatus: {
    [APP_ENUMS.productStatus.ACTIVE]: 'Active',
    [APP_ENUMS.productStatus.INACTIVE]: 'Inactive',
    [APP_ENUMS.productStatus.DRAFT]: 'Draft',
    [APP_ENUMS.productStatus.ARCHIVED]: 'Archived',
  },
  
  policyStatus: {
    [APP_ENUMS.policyStatus.DRAFT]: 'Draft',
    [APP_ENUMS.policyStatus.ACTIVE]: 'Active',
    [APP_ENUMS.policyStatus.EXPIRED]: 'Expired',
    [APP_ENUMS.policyStatus.CANCELLED]: 'Cancelled',
    [APP_ENUMS.policyStatus.CLAIMED]: 'Claimed',
  },
  
  quoteStatus: {
    [APP_ENUMS.quoteStatus.DRAFT]: 'Draft',
    [APP_ENUMS.quoteStatus.PENDING]: 'Pending Review',
    [APP_ENUMS.quoteStatus.APPROVED]: 'Approved',
    [APP_ENUMS.quoteStatus.REJECTED]: 'Rejected',
    [APP_ENUMS.quoteStatus.EXPIRED]: 'Expired',
  },
  
  claimStatus: {
    [APP_ENUMS.claimStatus.SUBMITTED]: 'Submitted',
    [APP_ENUMS.claimStatus.UNDER_REVIEW]: 'Under Review',
    [APP_ENUMS.claimStatus.APPROVED]: 'Approved',
    [APP_ENUMS.claimStatus.REJECTED]: 'Rejected',
    [APP_ENUMS.claimStatus.PAID]: 'Paid',
  },
  
  priority: {
    [APP_ENUMS.priority.LOW]: 'Low',
    [APP_ENUMS.priority.MEDIUM]: 'Medium',
    [APP_ENUMS.priority.HIGH]: 'High',
    [APP_ENUMS.priority.CRITICAL]: 'Critical',
  },
  
} as const;

// Status-to-Badge variant mappings for consistent styling
export const STATUS_BADGE_VARIANTS = {
  userStatus: {
    [APP_ENUMS.userStatus.ACTIVE]: 'success',
    [APP_ENUMS.userStatus.INACTIVE]: 'warning',
    [APP_ENUMS.userStatus.PENDING]: 'info',
    [APP_ENUMS.userStatus.SUSPENDED]: 'error',
  },
  
  organizationStatus: {
    [APP_ENUMS.organizationStatus.ACTIVE]: 'success',
    [APP_ENUMS.organizationStatus.INACTIVE]: 'warning',
    [APP_ENUMS.organizationStatus.PENDING_APPROVAL]: 'info',
  },
  
  productStatus: {
    [APP_ENUMS.productStatus.ACTIVE]: 'success',
    [APP_ENUMS.productStatus.INACTIVE]: 'warning',
    [APP_ENUMS.productStatus.DRAFT]: 'secondary',
    [APP_ENUMS.productStatus.ARCHIVED]: 'error',
  },
  
  policyStatus: {
    [APP_ENUMS.policyStatus.DRAFT]: 'secondary',
    [APP_ENUMS.policyStatus.ACTIVE]: 'success',
    [APP_ENUMS.policyStatus.EXPIRED]: 'warning',
    [APP_ENUMS.policyStatus.CANCELLED]: 'error',
    [APP_ENUMS.policyStatus.CLAIMED]: 'info',
  },
  
  quoteStatus: {
    [APP_ENUMS.quoteStatus.DRAFT]: 'secondary',
    [APP_ENUMS.quoteStatus.PENDING]: 'info',
    [APP_ENUMS.quoteStatus.APPROVED]: 'success',
    [APP_ENUMS.quoteStatus.REJECTED]: 'error',
    [APP_ENUMS.quoteStatus.EXPIRED]: 'warning',
  },
  
  claimStatus: {
    [APP_ENUMS.claimStatus.SUBMITTED]: 'info',
    [APP_ENUMS.claimStatus.UNDER_REVIEW]: 'warning',
    [APP_ENUMS.claimStatus.APPROVED]: 'success',
    [APP_ENUMS.claimStatus.REJECTED]: 'error',
    [APP_ENUMS.claimStatus.PAID]: 'success',
  },
  
  priority: {
    [APP_ENUMS.priority.LOW]: 'secondary',
    [APP_ENUMS.priority.MEDIUM]: 'info',
    [APP_ENUMS.priority.HIGH]: 'warning',
    [APP_ENUMS.priority.CRITICAL]: 'error',
  },
  
} as const;

// Helper function to get enum label
export const getEnumLabel = <K extends keyof typeof ENUM_LABELS>(
  enumCategory: K,
  value: string
): string => {
  const labels = ENUM_LABELS[enumCategory] as Record<string, string>;
  return labels[value] || value;
};

// Helper function to get status badge variant
export const getStatusBadgeVariant = <K extends keyof typeof STATUS_BADGE_VARIANTS>(
  statusCategory: K,
  value: string
): string => {
  const variants = STATUS_BADGE_VARIANTS[statusCategory] as Record<string, string>;
  return variants[value] || 'secondary';
};

// Helper to get options for dropdowns/selects
export const getEnumOptions = <K extends keyof typeof ENUM_LABELS>(
  enumCategory: K,
  enumValues: Record<string, string>
) => {
  return Object.entries(enumValues).map(([, value]) => ({
    value,
    label: getEnumLabel(enumCategory, value),
  }));
}; 